/**
 * Dealer admin resource config.
 *
 * Mirrors app/admin/dealer.rb 1-to-1:
 *   - 9 index filters (including OEM + service network scope selects whose
 *     options are passed as props from the Rails view)
 *   - 6 index columns
 *   - Tabbed show page: Profile (21 attributes) + Module Configuration (16 flags)
 *
 * The resource is read-only (canCreate/canModify are always false from the view).
 */
const dealerConfig = {
  name: 'Account',
  endpoint: '/internal_admin/admin_kit/dealers',

  // ── Index columns ──────────────────────────────────────────────────────────
  columns: [
    { name: 'decisiv_id', title: 'ID', type: 'link' },
    { name: 'name', title: 'Name' },
    { name: 'srm_account_id', title: 'SRM Account ID' },
    { name: 'dealer_type', title: 'Subscription Type' },
    { name: 'city', title: 'City' },
    { name: 'state', title: 'State' },
    { name: 'created_at', title: 'Created' },
  ],

  // ── Index filters ──────────────────────────────────────────────────────────
  //
  // `options` functions receive the Index component's props so the Rails view
  // can pass in dynamic option lists (dealer types, OEMs, service networks)
  // without a secondary fetch.
  filters: [
    { name: 'q[decisiv_id_cont]', label: 'Decisiv ID' },
    {
      name: 'q[dealer_type_eq]',
      label: 'Subscription Type',
      type: 'select',
      options: (props) =>
        (props.dealerTypes ?? []).map((t) => ({ label: t, value: t })),
    },
    { name: 'q[name_cont]', label: 'Name' },
    { name: 'q[city_cont]', label: 'City' },
    { name: 'q[state_cont]', label: 'State' },
    { name: 'q[business_entity_uri_cont]', label: 'Business Entity URI' },
    { name: 'q[group_id_eq]', label: 'Group ID' },
    {
      name: 'q[filter_by_oems]',
      label: 'OEM',
      type: 'select',
      options: (props) =>
        (props.oems ?? []).map((o) => ({ label: o.toUpperCase(), value: o })),
    },
    {
      name: 'q[filter_by_service_networks]',
      label: 'Service Network',
      type: 'select',
      options: (props) =>
        (props.serviceNetworks ?? []).map((n) => ({ label: n, value: n })),
    },
  ],

  // ── Tabbed show page ───────────────────────────────────────────────────────
  tabs: [
    {
      id: 'profile',
      title: 'Profile',
      attributes: [
        { name: 'decisiv_id', label: 'Decisiv ID' },
        { name: 'srm_account_id', label: 'SRM Account ID' },
        { name: 'dealer_type', label: 'Subscription Type' },
        { name: 'dealer_manufacturer_id', label: 'Dealer Manufacturer ID' },
        { name: 'oems', label: 'OEMs' },
        { name: 'service_networks', label: 'Service Networks' },
        { name: 'name', label: 'Name' },
        { name: 'group_id', label: 'Group ID' },
        { name: 'dms_id', label: 'DMS ID' },
        { name: 'owner', label: 'Owner' },
        { name: 'sponsored_by', label: 'Sponsored By' },
        { name: 'address1', label: 'Street Address' },
        { name: 'address2', label: 'Street Address 2' },
        { name: 'city', label: 'City' },
        { name: 'state', label: 'State' },
        { name: 'postal_code', label: 'Postal Code' },
        { name: 'phone', label: 'Phone' },
        { name: 'fax', label: 'Fax' },
        { name: 'email', label: 'Email' },
        { name: 'dealer_timezone', label: 'Timezone' },
        { name: 'created_at', label: 'Created At' },
        { name: 'business_entity_uri', label: 'Business Entity URI' },
      ],
    },
    {
      id: 'module_configuration',
      title: 'Module Configuration',
      attributes: [
        { name: 'api_enabled', label: 'API Enabled', type: 'boolean' },
        {
          name: 'broker_events_enabled',
          label: 'Broker Events Enabled',
          type: 'boolean',
        },
        {
          name: 'customer_markup_fields_enabled',
          label: 'Customer Markup Fields Enabled',
          type: 'boolean',
        },
        {
          name: 'decisiv_vision_notification_enabled',
          label: 'Decisiv Vision Notification Enabled',
          type: 'boolean',
        },
        {
          name: 'fleet_maintenance_operations_enabled',
          label: 'Fleet Maintenance Operations Enabled',
          type: 'boolean',
        },
        {
          name: 'inspection_admin_enabled',
          label: 'Inspection Admin Enabled',
          type: 'boolean',
        },
        {
          name: 'limit_customer_creation_enabled',
          label: 'Limit Customer Creation Enabled',
          type: 'boolean',
        },
        {
          name: 'miscellaneous_charges_from_dms_enabled',
          label: 'Miscellaneous Charges From DMS Enabled',
          type: 'boolean',
        },
        {
          name: 'motor_operations_enabled',
          label: 'Motor Operations Enabled',
          type: 'boolean',
        },
        {
          name: 'notifications_enabled',
          label: 'Notifications Enabled',
          type: 'boolean',
        },
        {
          name: 'parts_quote_enabled',
          label: 'Parts Quote Enabled',
          type: 'boolean',
        },
        {
          name: 'reservations_enabled',
          label: 'Reservations Enabled',
          type: 'boolean',
        },
        {
          name: 'service_location_billing_enabled',
          label: 'Service Location Billing Enabled',
          type: 'boolean',
        },
        {
          name: 'service_tracking_number_enabled',
          label: 'Service Tracking Number Enabled',
          type: 'boolean',
        },
        {
          name: 'shared_vehicle_history_enabled',
          label: 'Shared Vehicle History Enabled',
          type: 'boolean',
        },
        {
          name: 'travel_charges_enabled',
          label: 'Travel Charges Enabled',
          type: 'boolean',
        },
      ],
    },
  ],
};

export default dealerConfig;
